/**
 * Gift Wrapping Cart Management
 * Handles adding/removing gift wrapping products from the cart based on checkbox state
 */

class GiftWrappingManager {
  constructor() {
    // Prevent multiple instances
    if (GiftWrappingManager.instance) {
      return GiftWrappingManager.instance;
    }

    this.checkboxes = [];
    this.variantId = null;
    this.isProcessing = false;
    this.isInitialized = false;

    GiftWrappingManager.instance = this;
    this.init();
  }

  init() {
    // Find all gift wrapping checkboxes (cart drawer and cart page)
    const cartDrawerCheckbox = document.querySelector('#CartDrawer-GiftWrapping');
    const cartPageCheckbox = document.querySelector('#CartFooter-GiftWrapping');

    this.checkboxes = [cartDrawerCheckbox, cartPageCheckbox].filter(Boolean);

    if (this.checkboxes.length === 0) {
      this.isInitialized = false;
      return; // Exit if no checkboxes exist
    }

    // variantId may be empty when no gift-wrap product is configured in theme
    // settings (or the configured one was removed from the store). We still bind
    // listeners — we just track the user's selection via the cart attribute
    // (`attributes[Gift Wrapping]`) instead of adding/removing a product.
    const firstCheckbox = this.checkboxes[0];
    const rawVariantId = firstCheckbox.getAttribute('data-variant-id');
    this.variantId = rawVariantId && rawVariantId !== '0' ? rawVariantId : null;

    // Initialize each checkbox
    this.checkboxes.forEach((checkbox) => {
      // Only initialize once per checkbox element
      if (checkbox.dataset.giftWrappingInitialized === 'true') {
        return;
      }

      // Mark as initialized
      checkbox.dataset.giftWrappingInitialized = 'true';

      // Add event listener for checkbox changes
      checkbox.addEventListener('change', (event) => {
        this.handleCheckboxChange(event);
      });
    });

    this.isInitialized = true;

    // Check if gift wrapping is already in cart on page load
    this.checkInitialState();
  }

  /**
   * Toggle gift message input visibility based on checkbox state
   */
  toggleGiftMessage(isVisible) {
    // Find all gift message containers
    const giftMessageContainers = document.querySelectorAll('.gift-wrapping-cta__note');

    giftMessageContainers.forEach((container) => {
      if (isVisible) {
        container.style.display = 'block';
      } else {
        container.style.display = 'none';
      }
    });
  }

  /**
   * Update gift message visibility based on current checkbox state
   */
  updateGiftMessageVisibility() {
    const anyCheckboxChecked = this.checkboxes.some((checkbox) => checkbox && checkbox.checked);
    this.toggleGiftMessage(anyCheckboxChecked);
  }

  /**
   * Check if gift wrapping is already in the cart and update checkboxes accordingly
   */
  async checkInitialState() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // With a product configured, the cart line item is the source of truth.
      // Without one, fall back to the cart attribute.
      let isChecked;
      if (this.variantId) {
        const giftWrapItem = cart.items.find((item) => item.variant_id.toString() === this.variantId.toString());
        isChecked = !!giftWrapItem;
      } else {
        isChecked = cart.attributes && cart.attributes['Gift Wrapping'] === 'true';
      }

      // Update all checkboxes to match cart state
      this.checkboxes.forEach((checkbox) => {
        checkbox.checked = isChecked;
      });

      // Update gift message visibility
      this.toggleGiftMessage(isChecked);
    } catch (error) {
      console.error('Error checking initial gift wrapping state:', error);
    }
  }

  /**
   * Handle checkbox state changes
   */
  async handleCheckboxChange(event) {
    if (this.isProcessing) {
      event.preventDefault();
      return;
    }

    const isChecked = event.target.checked;
    this.isProcessing = true;

    // Sync all other checkboxes to match the changed one
    this.checkboxes.forEach((checkbox) => {
      if (checkbox !== event.target) {
        checkbox.checked = isChecked;
      }
    });

    // Toggle gift message visibility
    this.toggleGiftMessage(isChecked);

    try {
      if (this.variantId) {
        if (isChecked) {
          await this.addGiftWrapping();
        } else {
          await this.removeGiftWrapping();
        }
      } else {
        // No product configured — just write the cart attribute so the
        // selection persists across page loads / cart-drawer reloads.
        await this.updateGiftWrappingAttribute(isChecked);
      }
    } catch (error) {
      // Don't revert the checkbox or surface a user-facing toast. The cart
      // attribute will still ride along on the next cart-form submit
      // (checkout click) thanks to `name="attributes[Gift Wrapping]"`.
      // This is typically a merchant config issue (e.g., the configured
      // product was deleted), not something the shopper can fix.
      console.error('Error updating gift wrapping:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Set the cart attribute directly via /cart/update.js — used when no gift
   * wrap product is configured.
   */
  async updateGiftWrappingAttribute(isChecked) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: { 'Gift Wrapping': isChecked ? 'true' : '' },
      }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to update gift wrapping cart attribute');
    }
  }

  /**
   * Add gift wrapping to cart
   */
  async addGiftWrapping() {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: this.variantId,
        quantity: 1,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add gift wrapping');
    }

    const result = await response.json();
    console.log('Gift wrapping added:', result);

    // Update cart display
    this.updateCartDisplay();
  }

  /**
   * Remove gift wrapping from cart
   */
  async removeGiftWrapping() {
    // First, get current cart to find the line item
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    // Find the gift wrapping item
    const giftWrapItem = cart.items.find((item) => item.variant_id.toString() === this.variantId.toString());

    if (!giftWrapItem) {
      console.log('Gift wrapping not found in cart');
      return;
    }

    // Remove using cart/change.js endpoint
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: this.variantId,
        quantity: 0,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to remove gift wrapping');
    }

    const result = await response.json();
    console.log('Gift wrapping removed:', result);

    // Update cart display
    this.updateCartDisplay();
  }

  /**
   * Update cart display after changes
   */
  updateCartDisplay() {
    // Trigger cart update events that the theme might be listening for
    if (typeof window.CartJS !== 'undefined' && window.CartJS.cart) {
      // If CartJS is available, use it
      window.CartJS.cart.update();
    } else {
      // Otherwise, try to reload cart drawer content
      this.reloadCartDrawer();
      this.reloadCartPage();
    }
  }

  /**
   * Reload cart drawer content
   */
  async reloadCartDrawer() {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update cart drawer items
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      const newCartDrawerItems = doc.querySelector('cart-drawer-items');

      if (cartDrawerItems && newCartDrawerItems) {
        cartDrawerItems.innerHTML = newCartDrawerItems.innerHTML;
      }

      // Update cart drawer footer
      const cartDrawerFooter = document.querySelector('.cart-drawer__footer');
      const newCartDrawerFooter = doc.querySelector('.cart-drawer__footer');

      if (cartDrawerFooter && newCartDrawerFooter) {
        cartDrawerFooter.innerHTML = newCartDrawerFooter.innerHTML;
      }

      // Reinitialize if new checkbox elements were loaded
      const newCartDrawerCheckbox = document.querySelector('#CartDrawer-GiftWrapping');
      const newCartPageCheckbox = document.querySelector('#CartFooter-GiftWrapping');

      if (
        (newCartDrawerCheckbox && newCartDrawerCheckbox.dataset.giftWrappingInitialized !== 'true') ||
        (newCartPageCheckbox && newCartPageCheckbox.dataset.giftWrappingInitialized !== 'true')
      ) {
        this.init();
      } else {
        // If checkboxes are already initialized, just update gift message visibility
        this.updateGiftMessageVisibility();
      }
    } catch (error) {
      console.error('Error reloading cart drawer:', error);
      // Fallback: reload the page
      window.location.reload();
    }
  }

  /**
   * Reload cart page content
   */
  async reloadCartPage() {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=main-cart-items`);
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update cart items
      const cartItems = document.querySelector('#main-cart-items');
      const newCartItems = doc.querySelector('#main-cart-items');

      if (cartItems && newCartItems) {
        cartItems.innerHTML = newCartItems.innerHTML;
      }

      // Update cart footer
      const cartFooter = document.querySelector('#main-cart-footer');
      const newCartFooter = doc.querySelector('#main-cart-footer');

      if (cartFooter && newCartFooter) {
        cartFooter.innerHTML = newCartFooter.innerHTML;
      }

      // Reinitialize if new checkbox elements were loaded
      const newCartDrawerCheckbox = document.querySelector('#CartDrawer-GiftWrapping');
      const newCartPageCheckbox = document.querySelector('#CartFooter-GiftWrapping');

      if (
        (newCartDrawerCheckbox && newCartDrawerCheckbox.dataset.giftWrappingInitialized !== 'true') ||
        (newCartPageCheckbox && newCartPageCheckbox.dataset.giftWrappingInitialized !== 'true')
      ) {
        this.init();
      } else {
        // If checkboxes are already initialized, just update gift message visibility
        this.updateGiftMessageVisibility();
      }
    } catch (error) {
      console.error('Error reloading cart page:', error);
      // Fallback: reload the page
      window.location.reload();
    }
  }

  /**
   * Show error message to user
   */
  showError(message) {
    // You can customize this to match your theme's error display style
    const errorDiv = document.createElement('div');
    errorDiv.className = 'gift-wrapping-error';
    errorDiv.style.cssText = `
      background: #f8d7da;
      color: #721c24;
      padding: 10px;
      border-radius: 4px;
      margin-top: 10px;
      font-size: 14px;
    `;
    errorDiv.textContent = message;

    // Insert error message near the checkbox
    const giftWrappingCta = document.querySelector('.gift-wrapping-cta');
    if (giftWrappingCta) {
      giftWrappingCta.appendChild(errorDiv);

      // Remove error after 5 seconds
      setTimeout(() => {
        if (errorDiv.parentNode) {
          errorDiv.parentNode.removeChild(errorDiv);
        }
      }, 5000);
    }
  }
}

// Initialize gift wrapping manager
function initGiftWrapping() {
  if (!window.giftWrappingManager) {
    window.giftWrappingManager = new GiftWrappingManager();
  }
}

// Initialize when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGiftWrapping);
} else {
  initGiftWrapping();
}
